import { useEffect, useState } from "react";
import { api, ApiError } from "../../lib/api.js";
import { formatDate } from "../../lib/format.js";
import { AdminTabs } from "../../components/AdminTabs.js";
import { Modal, PageSpinner, RoleBadge, StatusBadge } from "../../components/ui.js";
import { IconCheck, IconCopy } from "../../components/icons.js";

interface AdminInvite {
  id: string;
  email: string | null;
  role: "admin" | "member" | "trainer";
  status: "pending" | "used" | "expired" | "revoked";
  note: string | null;
  createdByUsername: string | null;
  createdAt: string;
  expiresAt: string;
  usedAt: string | null;
}

export function AdminInvitesPage() {
  const [invites, setInvites] = useState<AdminInvite[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createdLink, setCreatedLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Create form
  const [role, setRole] = useState("member");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [expiresInDays, setExpiresInDays] = useState(14);
  const [creating, setCreating] = useState(false);

  async function load() {
    try {
      const data = await api.get<{ invitations: AdminInvite[] }>("/api/admin/invitations");
      setInvites(data.invitations);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load invitations.");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function createInvite(e: React.FormEvent) {
    e.preventDefault();
    setCreating(true);
    setError(null);
    try {
      const result = await api.post<{ token: string }>("/api/admin/invitations", {
        role,
        email: email || undefined,
        note: note || undefined,
        expiresInDays
      });
      const link = `${window.location.origin}/invite/${result.token}`;
      setCreatedLink(link);
      setShowCreate(false);
      setEmail("");
      setNote("");
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not create invitation.");
    } finally {
      setCreating(false);
    }
  }

  async function revoke(id: string) {
    setError(null);
    try {
      await api.post(`/api/admin/invitations/${id}/revoke`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not revoke.");
    }
  }

  async function copyLink() {
    if (!createdLink) return;
    try {
      await navigator.clipboard.writeText(createdLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard may be unavailable; the link is selectable */
    }
  }

  return (
    <div>
      <div className="page-header">
        <div className="row-between">
          <div>
            <h1>Administration</h1>
            <p className="subtitle">Invitations to join ClovaForm.</p>
          </div>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            New invitation
          </button>
        </div>
      </div>
      <AdminTabs />

      {error && <div className="alert alert-danger">{error}</div>}

      {createdLink && (
        <div className="card mt-4" style={{ borderColor: "var(--accent)" }}>
          <div className="card-header">
            <span className="card-title">Invitation link created</span>
            <button className="btn btn-sm btn-ghost" onClick={() => setCreatedLink(null)}>
              Done
            </button>
          </div>
          <p className="muted text-sm">
            Share this link with the person you’re inviting. It’s shown once and
            is single-use — ClovaForm stores only a hash, so it can’t be
            recovered later.
          </p>
          <div className="token-box mt-4">
            <span className="grow">{createdLink}</span>
            <button className="btn btn-sm" onClick={copyLink}>
              {copied ? <IconCheck width={16} height={16} /> : <IconCopy width={16} height={16} />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        </div>
      )}

      {!invites ? (
        <PageSpinner />
      ) : invites.length === 0 ? (
        <div className="card center muted mt-4">No invitations yet.</div>
      ) : (
        <>
          <div className="data-table-wrap card mt-4" style={{ padding: 0 }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Email / note</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Expires</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invites.map((inv) => (
                  <tr key={inv.id}>
                    <td>
                      <div>{inv.email ?? <span className="dim">Any email</span>}</div>
                      {inv.note && <div className="dim text-xs">{inv.note}</div>}
                    </td>
                    <td>
                      <RoleBadge role={inv.role} />
                    </td>
                    <td>
                      <StatusBadge status={inv.status} />
                    </td>
                    <td className="dim">{formatDate(inv.createdAt)}</td>
                    <td className="dim">{formatDate(inv.expiresAt)}</td>
                    <td style={{ textAlign: "right" }}>
                      {inv.status === "pending" && (
                        <button className="btn btn-sm btn-danger" onClick={() => revoke(inv.id)}>
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="table-cards mt-4">
            {invites.map((inv) => (
              <div className="table-card" key={inv.id}>
                <div className="row-between">
                  <div style={{ fontWeight: 600 }}>{inv.email ?? "Any email"}</div>
                  <StatusBadge status={inv.status} />
                </div>
                {inv.note && <div className="dim text-xs mt-2">{inv.note}</div>}
                <div className="tc-row">
                  <span className="tc-label">Role</span>
                  <RoleBadge role={inv.role} />
                </div>
                <div className="tc-row">
                  <span className="tc-label">Expires</span>
                  <span>{formatDate(inv.expiresAt)}</span>
                </div>
                {inv.status === "pending" && (
                  <button
                    className="btn btn-sm btn-danger btn-block mt-4"
                    onClick={() => revoke(inv.id)}
                  >
                    Revoke
                  </button>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {showCreate && (
        <Modal title="New invitation" onClose={() => setShowCreate(false)}>
          <form onSubmit={createInvite}>
            <div className="field">
              <label htmlFor="inv-role">Role</label>
              <select
                id="inv-role"
                className="select"
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                <option value="member">Member</option>
                <option value="trainer">Trainer</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="inv-email">Restrict to email (optional)</label>
              <input
                id="inv-email"
                className="input"
                type="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
              />
              <span className="hint">
                If set, only this address can redeem the invitation.
              </span>
            </div>
            <div className="field">
              <label htmlFor="inv-note">Note (optional)</label>
              <input
                id="inv-note"
                className="input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. my trainer"
              />
            </div>
            <div className="field">
              <label htmlFor="inv-expiry">Valid for (days)</label>
              <input
                id="inv-expiry"
                className="input"
                type="number"
                min={1}
                max={90}
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(Number(e.target.value))}
              />
            </div>
            <div className="row mt-4">
              <button className="btn btn-primary grow" type="submit" disabled={creating}>
                {creating ? "Creating…" : "Create invitation"}
              </button>
              <button type="button" className="btn" onClick={() => setShowCreate(false)}>
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
